import React, { useState, useRef } from "react";
import PropTypes from "prop-types";

import _ from "lodash";


import { useTranslation } from "react-i18next";

import { mapColumnsToGroupFields } from "../../../utils/mappers/groupSettings";

import {
    Input,
    Segment,
    Form,
    Grid,
    Button,
    Dropdown,
    Radio,
} from "semantic-ui-react";

import ADURangeSlider from "../ADURangeSlider/ADURangeSlider";
import SimpleRangeSlider from "../SimpleRangeSlider/SimpleRangeSlider";

import { round } from "../../../utils/format";

import {
    variabilityFactorMin,
    variabilityFactorMax,
    variabilityFactorStep,
    leadTimeFactorMin,
    leadTimeFactorMax,
    leadTimeFactorStep,
} from "../../../utils/constants";

import "./RulesSettings.css";

const RulesSettings = ({
    selectedGroup,
    columns,
    items,
    ruleData,
    setRuleData,
    onCancel,
    rulesDisabled,
    materialMethodOptions,
    intermitentDemandOptions,
}) => {
    const { t } = useTranslation();

    const groupFields = mapColumnsToGroupFields(t, columns, items);

    const def = {};
    for (let field of groupFields) {
        def[[field.accessor]] = {
            name: field.name,
            accessor: field.accessor,
            values: ["all"],
        };
    }

    const initialGroupData = _.isEmpty(selectedGroup)
        ? { ...def }
        : { ...selectedGroup };
    for (let field of groupFields) {
        if (_.isNil(initialGroupData[[field.accessor]])) {
            initialGroupData[[field.accessor]] = {
                name: field.name,
                accessor: field.accessor,
                values: ["all"],
            };
        }
    }

    const getVariabilityTag = (value) => {
        let text;
        if(_.isNil(value)){
            text = t("groupSettings.rules.variability.tags.medium");
            return `${text} (0.5)`
        }
        if (value > 0.9) {
            text = t("groupSettings.rules.variability.tags.extremelyHigh");
        } else if (value > 0.7) {
            text = t("groupSettings.rules.variability.tags.veryHigh");
        } else if (value > 0.5) {
            text = t("groupSettings.rules.variability.tags.high");
        } else if (value > 0.3) {
            text = t("groupSettings.rules.variability.tags.medium");
        } else if (value > 0.1) {
            text = t("groupSettings.rules.variability.tags.low");
        } else {
            text = t("groupSettings.rules.variability.tags.veryLow");
        }
        return `${text} (${value})`;
    };

    const getLeadTimeTag = (value) => {
        let text;
        if(_.isNil(value)){
            text = t("groupSettings.rules.leadTime.tags.medium");
            return `${text} (0.5)`
        }
        if (value > 0.9) {
            text = t("groupSettings.rules.leadTime.tags.extremelyShort");
        } else if (value > 0.7) {
            text = t("groupSettings.rules.leadTime.tags.veryShort");
        } else if (value > 0.5) {
            text = t("groupSettings.rules.leadTime.tags.short");
        } else if (value > 0.3) {
            text = t("groupSettings.rules.leadTime.tags.medium");
        } else if (value > 0.1) {
            text = t("groupSettings.rules.leadTime.tags.long");
        } else {
            text = t("groupSettings.rules.leadTime.tags.veryLong");
        }
        return `${text} (${value})`;
    };

    const incrementLeadTimeFactor = () => {
        const newValue = round(
            parseFloat(
                _.isNil(ruleData.leadTimeFactor) ? 0.5 : ruleData.leadTimeFactor) + leadTimeFactorStep,
            2
        );
        setRuleData({
            ...ruleData,
            leadTimeFactor: Math.min(newValue, leadTimeFactorMax),
        });
    };

    const decreaseLeadTimeFactor = () => {
        const newValue = round(
            parseFloat(
                _.isNil(ruleData.leadTimeFactor) ? 0.5 : ruleData.leadTimeFactor ) - leadTimeFactorStep,
            2
        );
        setRuleData({
            ...ruleData,
            leadTimeFactor: Math.max(newValue, leadTimeFactorMin),
        });
    };

    const incrementVariabilityFactor = () => {
        const newValue = round(
            parseFloat(
                _.isNil(ruleData.variabilityFactor) ? 0.5 : ruleData.variabilityFactor) + variabilityFactorStep,
            2
        );
        setRuleData({
            ...ruleData,
            variabilityFactor: Math.min(newValue, variabilityFactorMax),
        });
    };

    const decreaseVariabilityFactor = () => {
        const newValue = round(
            parseFloat(
                _.isNil(ruleData.variabilityFactor) ? 0.5 : ruleData.variabilityFactor) - variabilityFactorStep,
            2
        );
        setRuleData({
            ...ruleData,
            variabilityFactor: Math.max(newValue, variabilityFactorMin),
        });
    };

    const initialMarked = {};
    groupFields.forEach((field) => (initialMarked[field.accessor] = true));
    const [intermitentDemandActive, setIntermitentDemandActive] = useState(!_.isNil(ruleData.intermittentDemand))
    const prevFactors = useRef({
        leadTimeFactor: ruleData.leadTimeFactor,
        variabilityFactor: ruleData.variabilityFactor,
    });
    const changeIntermittentDemand = () => {
        if (!intermitentDemandActive) {
            prevFactors.current = {
                leadTimeFactor: ruleData.leadTimeFactor,
                variabilityFactor: ruleData.variabilityFactor,
            };
            setRuleData({
                ...ruleData,
                leadTimeFactor: null,
                variabilityFactor: null,
            });
        } else {
            setRuleData({
                ...ruleData,
                intermittentDemand: null,
                leadTimeFactor: prevFactors.current.leadTimeFactor ?? 0.5,
                variabilityFactor: prevFactors.current.variabilityFactor ?? 0.5,
            });
        }
        setIntermitentDemandActive(!intermitentDemandActive);
    }
    const getMaterialMethod = () =>{
        if (!['rules', 'event', 'subgroup'].some(
                subgroupTag => ruleData.materialMethod.includes(
                subgroupTag))){
                    return ruleData.materialMethod
                }

        else if(ruleData && ruleData.description === 'non_buffered-subgroup'){
            return 'non_buffered'
        }
        else{
            return 'buffered'
        }
    }

    const nonBufferedSelected = getMaterialMethod() === "non_buffered";
    const otherFieldsDisabled = rulesDisabled || nonBufferedSelected;

    return (

        <Segment className="rules-group">
            <Grid>
                <Grid.Row>
                    <Grid.Column>
                        <label className="rules-title">
                            {t("groupSettings.rules.title")}
                        </label>
                        <Form.Group widths={2}>
                                <Form.Field className="method-field">
                                    <label data-disabled={rulesDisabled 
                                    ? "true": "false"}>
                                        {t("groupSettings.rules.materialMethod.title")}
                                    </label>
                                    <Dropdown
                                        className="method-dropdown"
                                        selection
                                        compact
                                        disabled={rulesDisabled}
                                        value={getMaterialMethod()}
                                        options={materialMethodOptions}
                                        onChange={(e, data) =>{
                                            if(['rules', 'event', 'subgroup'].some(
                                                subgroupTag => ruleData.materialMethod.includes(
                                                    subgroupTag)) && data.value === "non_buffered"){
                                                        setRuleData({
                                                            ...ruleData,
                                                            description: "non_buffered-subgroup",
                                            })
                                            }
                                            else{
                                                setRuleData({
                                                ...ruleData,
                                                materialMethod: data.value,
                                                description: ""
                                            })
                                            }
                                        }
                                        }
                                    ></Dropdown>
                                    <div className='inline-field-justify-left'>
                                        <label data-disabled={otherFieldsDisabled
                                            ? "true": "false"}>
                                            {t("groupSettings.rules.intermittent_demand")}
                                        </label>
                                        <Radio
                                            checked={intermitentDemandActive}
                                            disabled={otherFieldsDisabled}
                                            toggle
                                            onChange={changeIntermittentDemand}
                                            />
                                    </div>
                                        {intermitentDemandActive?
                                    <Dropdown
                                        className="method-dropdown"
                                        selection
                                        compact
                                        disabled={otherFieldsDisabled || !intermitentDemandActive}
                                        value={ruleData.intermittentDemand}
                                        options={intermitentDemandOptions}
                                        onChange={(e, data) =>
                                            setRuleData({
                                                ...ruleData,
                                                intermittentDemand: data.value,
                                            })
                                        }
                                    ></Dropdown>: null}
                                </Form.Field>

                            <Form.Field className="adu-length">
                                <label data-disabled={otherFieldsDisabled
                                    ? "true": "false"}>{t("groupSettings.rules.aduLength")}</label>
                                <ADURangeSlider
                                    startInitialValue={ruleData.aduMeasurementStart}
                                    endInitialValue={ruleData.aduMeasurementEnd}
                                    onChange={(low, high) => {
                                        setRuleData({
                                            ...ruleData,
                                            aduMeasurementStart: low,
                                            aduMeasurementEnd: high,
                                        });
                                    }}
                                    rulesDisabled={otherFieldsDisabled}
                                />
                            </Form.Field>
                        </Form.Group>

                        <Form.Group widths={2}>
                            <Form.Field className="lead-time-slider">
                                <label data-disabled={otherFieldsDisabled || intermitentDemandActive
                                    ? "true": "false"}>
                                    {t("groupSettings.rules.leadTime.title")}
                                </label>

                                <Grid>
                                    <Grid.Column width={16}>
                                        <SimpleRangeSlider
                                            step={leadTimeFactorStep}
                                            min={leadTimeFactorMin}
                                            max={leadTimeFactorMax}
                                            onChange={(value) => {
                                                setRuleData({
                                                    ...ruleData,
                                                    leadTimeFactor: round(parseFloat(value), 1),
                                                });
                                            }}
                                            initialValue={!_.isNil(ruleData.leadTimeFactor) ?
                                                ruleData.leadTimeFactor : 0.5 }
                                            rulesDisabled={otherFieldsDisabled || intermitentDemandActive}
                                        />
                                    </Grid.Column>
                                    <Grid.Column textAlign={"center"} width={16}>
                                        <Input
                                            className="lead-time-controls"
                                            value={getLeadTimeTag(ruleData.leadTimeFactor)}
                                            action
                                            disabled={otherFieldsDisabled || intermitentDemandActive}
                                        >
                                            <Button
                                                positive
                                                icon="minus"
                                                size="mini"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    decreaseLeadTimeFactor();
                                                }}
                                                disabled={otherFieldsDisabled || intermitentDemandActive}
                                            />
                                            <input type="text" />
                                            <Button
                                                positive
                                                icon="plus"
                                                size="mini"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    incrementLeadTimeFactor();
                                                }}
                                                disabled={otherFieldsDisabled || intermitentDemandActive}
                                            />
                                        </Input>
                                    </Grid.Column>
                                </Grid>
                            </Form.Field>

                            <Form.Field className="variability-slider">
                                <label
                                    data-disabled={otherFieldsDisabled || intermitentDemandActive
                                    ? "true": "false"}>
                                    {t("groupSettings.rules.variability.title")}
                                </label>
                                <Grid>
                                    <Grid.Column width={16}>
                                        <SimpleRangeSlider
                                            step={variabilityFactorStep}
                                            min={variabilityFactorMin}
                                            max={variabilityFactorMax}
                                            onChange={(value) => {
                                                setRuleData({
                                                    ...ruleData,
                                                    variabilityFactor: round(
                                                        parseFloat(value),
                                                        1
                                                    ),
                                                });
                                            }}
                                            initialValue={!_.isNil(ruleData.variabilityFactor) ?
                                                 ruleData.variabilityFactor: 0.5}
                                            rulesDisabled={otherFieldsDisabled || intermitentDemandActive}
                                        />
                                    </Grid.Column>
                                    <Grid.Column textAlign={"center"} width={16}>
                                        <Input
                                            className="lead-time-controls"
                                            value={getVariabilityTag(ruleData.variabilityFactor)}
                                            action
                                            disabled={otherFieldsDisabled || intermitentDemandActive}
                                        >
                                            <Button
                                                disabled={otherFieldsDisabled || intermitentDemandActive}
                                                positive
                                                icon="minus"
                                                size="mini"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    decreaseVariabilityFactor();
                                                }}
                                            />
                                            <input type="text" />
                                            <Button
                                                disabled={otherFieldsDisabled || intermitentDemandActive}
                                                positive
                                                icon="plus"
                                                size="mini"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    incrementVariabilityFactor();
                                                }}
                                            />
                                        </Input>
                                    </Grid.Column>
                                </Grid>
                            </Form.Field>
                        </Form.Group>
                    </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                    <Grid.Column>
                        {/* <Divider /> */}
                        <div className={"action-buttons"}>
                            <Button disabled={rulesDisabled} onClick={onCancel}>{t("cancel")}</Button>
                            <Button disabled={rulesDisabled} positive className="submit-group" type="submit">
                                {t("save")}
                            </Button>
                        </div>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Segment>
    );
};

RulesSettings.propTypes = {
    selectedGroup: PropTypes.object,
    columns: PropTypes.object,
    items: PropTypes.array,
    ruleData: PropTypes.object,
    setRuleData: PropTypes.func,
    onCancel: PropTypes.func,
    rulesDisabled: PropTypes.bool,
    materialMethodOptions: PropTypes.array,
    intermitentDemandOptions: PropTypes.array,
    groupNames: PropTypes.arrayOf(PropTypes.string),
    loading: PropTypes.bool,
    groups: PropTypes.array,
};

RulesSettings.defaultProps = {
    selectedGroup: null,
    columns: [],
    items: [],
    groupNames: [],
    onSave: _.noop,
    onCancel: _.noop,
    loading: false,
    rulesDisabled: false,
    groups: [],
};

export default RulesSettings;